import type { UserDetail } from '@planuze/admin-contracts';
import { Button, EmptyState, InlineAlert } from '@planuze/ui';
import { Link } from '@tanstack/react-router';
import { ExternalLink, FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useUsersContent } from '../../../content/sections/users.js';
import { fmtDateTime } from '../../../lib/format.js';
import { FacetMultiSelect } from '../../filters/facet-multi-select.js';

type AuditEntry = UserDetail['recentAudit'][number];

interface Props {
  readonly audit: readonly AuditEntry[];
  readonly userId: string;
}

/* Preview das ~20 ações administrativas recentes SOBRE o usuário (ADR-0708/0853).
   Não representa login nem atividade da própria conta. A trilha completa (paginada por cursor)
   fica na tela `/audit`, alcançada pelo deep-link "Ver auditoria completa" já escopado pelo alvo
   (`targetType=user` + `targetId`, ADR-0713). A `reason` (justificativa livre da ação) é extraída
   da metadata JSONB do audit log no agregador e exibida por entrada quando presente. */
export function AuditTab({ audit, userId }: Props) {
  const copy = useUsersContent();
  const [selected, setSelected] = useState<string[]>([]);

  const fullAuditLink = (
    <Button asChild variant="outline" size="sm">
      <Link to="/audit" search={{ targetType: 'user', targetId: userId }}>
        {copy.auditTab.viewFullAudit}
        <ExternalLink className="size-4" aria-hidden="true" />
      </Link>
    </Button>
  );

  /* Opções de ação deduplicadas das entradas carregadas. */
  const actionOptions = useMemo(() => {
    const seen = new Set(audit.map((entry) => entry.action));
    return [...seen].sort().map((act) => ({ value: act, label: act }));
  }, [audit]);

  const rows = useMemo(
    () =>
      selected.length === 0 ? audit : audit.filter((entry) => selected.includes(entry.action)),
    [audit, selected],
  );

  if (audit.length === 0) {
    return (
      <div className="space-y-3">
        <InlineAlert variant="info">{copy.auditTab.scopeDescription}</InlineAlert>
        <EmptyState
          icon={<FileText className="size-6" />}
          title={copy.auditTab.emptyTitle}
          primaryAction={fullAuditLink}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <InlineAlert variant="info">{copy.auditTab.scopeDescription}</InlineAlert>
      <div className="flex flex-wrap items-center gap-3">
        <FacetMultiSelect
          label={copy.auditTab.actionLabel}
          values={selected}
          onChange={setSelected}
          options={actionOptions}
        />
        <span className="text-sm text-muted-foreground tabular-nums">
          {copy.common.countOf(rows.length, audit.length)}
        </span>
        <div className="ml-auto">{fullAuditLink}</div>
      </div>
      {rows.length === 0 ? (
        <EmptyState
          icon={<FileText className="size-6" />}
          title={copy.common.noResultsTitle}
          description={copy.auditTab.emptyFilteredDescription}
          primaryAction={
            <Button variant="outline" onClick={() => setSelected([])}>
              {copy.common.clearFilters}
            </Button>
          }
        />
      ) : (
        <ul className="space-y-2">
          {rows.map((entry, index) => (
            <li
              key={`${entry.ts}-${index}`}
              className="space-y-1 rounded-md border border-border bg-card p-3"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-sm text-muted-foreground">{entry.action}</span>
                  <span className="text-sm text-foreground">
                    {entry.actorEmail ?? entry.actorId}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">{fmtDateTime(entry.ts)}</span>
              </div>
              {entry.reason !== null && (
                <p className="text-sm text-muted-foreground">{entry.reason}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
